import { useRef } from 'react'
import { X } from 'lucide-react'

import { GO_TO_SHORTCUTS } from '@/components/layout/nav-config'
import { useFocusTrap } from '@/lib/keyboard/use-focus-trap'

export interface ShortcutsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface Shortcut {
  keys: string
  description: string
}

/** Mirrors docs/00-roadmap.md Part 3. Keep the two in step. */
const SECTIONS: ReadonlyArray<{ title: string; shortcuts: readonly Shortcut[] }> = [
  {
    title: 'Global',
    shortcuts: [
      { keys: '/', description: 'Focus search' },
      { keys: 'Ctrl K', description: 'Command palette' },
      { keys: 'Ctrl S', description: 'Save the current form' },
      { keys: 'Ctrl ↵', description: 'Save and create another' },
      { keys: 'Esc', description: 'Close the dialog or drawer' },
      { keys: '?', description: 'Show this cheatsheet' },
    ],
  },
  {
    title: 'Go to',
    shortcuts: GO_TO_SHORTCUTS.map((shortcut) => ({
      keys: `g ${shortcut.key}`,
      description: shortcut.label,
    })),
  },
  {
    title: 'Fields',
    shortcuts: [
      { keys: 'Tab', description: 'Next field' },
      { keys: 'Shift Tab', description: 'Previous field' },
      { keys: '↵', description: 'Commit and advance to the next field' },
      { keys: 'Esc', description: 'Close a dropdown, keeping the previous value' },
    ],
  },
  {
    title: 'Dropdowns',
    shortcuts: [
      { keys: '↓ ↑', description: 'Move the highlight' },
      { keys: '↵', description: 'Select and move to the next field' },
      { keys: 'Ctrl ↵', description: 'Create a new record from what you typed' },
    ],
  },
  {
    title: 'Line-item grids',
    shortcuts: [
      { keys: 'Tab', description: 'Next cell; adds a row from the last cell' },
      { keys: '↵', description: 'Next cell; commits the row and starts another' },
      { keys: 'Ctrl D', description: 'Duplicate the row above' },
      { keys: 'Ctrl Del', description: 'Delete the current row' },
      { keys: '↑ ↓', description: 'Move between rows in the same column' },
    ],
  },
]

function Keys({ keys }: { keys: string }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {keys.split(' ').map((key, index) => (
        <kbd
          key={`${key}-${index}`}
          className="rounded border border-border bg-surface-sunken px-1.5 py-0.5 font-mono text-[11px] text-fg-secondary"
        >
          {key}
        </kbd>
      ))}
    </span>
  )
}

/** The `?` cheatsheet. Read-only, so Esc and the close button are the whole UI. */
export function ShortcutsDialog({ open, onOpenChange }: ShortcutsDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const close = () => onOpenChange(false)

  useFocusTrap(open, dialogRef, close)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-[3px]" aria-hidden onClick={close} />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-dialog-title"
        className="relative z-10 flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-card border border-border bg-surface-raised shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 id="shortcuts-dialog-title" className="text-base font-semibold text-fg">
            Keyboard shortcuts
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label="Close shortcuts"
            className="grid size-8 place-items-center rounded-control text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="grid gap-6 overflow-y-auto px-5 py-5 sm:grid-cols-2">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {section.title}
              </h3>
              <dl className="flex flex-col gap-1.5">
                {section.shortcuts.map((shortcut) => (
                  <div
                    key={`${shortcut.keys}-${shortcut.description}`}
                    className="flex items-baseline justify-between gap-3"
                  >
                    <dt className="min-w-0 text-sm text-fg-secondary">{shortcut.description}</dt>
                    <dd>
                      <Keys keys={shortcut.keys} />
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <p className="border-t border-border px-5 py-3 text-xs text-fg-muted">
          On macOS, Ctrl is ⌘.
        </p>
      </div>
    </div>
  )
}
